import { useEffect, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { AgentHandoff } from '../agent/AgentChatWidget';
import { loadConversationId } from '../agent/conversationId';
import './RdsMiaSummary.css';

export type RdsSummaryKind = 'client' | 'vin';

/** What Mia is doing while the summary generates: several seconds on a cache miss, and a
 *  status that moves reads as work in progress where a static spinner reads as stuck. */
const STEPS: Record<RdsSummaryKind, string[]> = {
  client: [
    'Reading Core standing and inspections…',
    'Going through their inventory by bucket…',
    'Checking bids, offers and counters on each unit…',
  ],
  vin: [
    'Reading price against MMR…',
    'Going through bids, offers and seller counters…',
    'Checking how earlier listings were set up and ended…',
  ],
};
const STEP_MS = 4000;
const FALLBACK_PROMPT: Record<RdsSummaryKind, string> = {
  client: 'Where should I focus with this dealer?',
  vin: 'Where should I focus with this unit?',
};

interface RdsMiaSummaryProps {
  kind: RdsSummaryKind;
  /** Seller UID for `client`, VIN for `vin`. */
  id: string;
  /** Opens the Client View chat on the summary. Omitted where there is no chat (Main View). */
  onContinue?: (handoff: AgentHandoff) => void;
}

/**
 * Mia's "Where to focus" for a dealer or a unit (RDS-3.5), generated when the pop-out opens.
 * One cached model call on the server, not a chat turn, so opening dealers and units never takes
 * the rep's Mia slot. "Continue in Mia" seeds the summary and the record behind it into the RDS
 * thread first, so a follow-up question has it.
 */
export function RdsMiaSummary({ kind, id, onContinue }: RdsMiaSummaryProps) {
  const [text, setText] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'done' | 'error'>('loading');
  const [step, setStep] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setPhase('loading');
    setText(null);
    setStep(0);
    const timer = window.setInterval(
      () => setStep((s) => Math.min(s + 1, STEPS[kind].length - 1)), STEP_MS,
    );
    siftApiClient.getRdsSummary(kind, id, controller.signal)
      .then((res) => {
        setText(res.text);
        setPhase('done');
      })
      .catch(() => {
        if (!controller.signal.aborted) setPhase('error');
      })
      .finally(() => window.clearInterval(timer));
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [kind, id, attempt]);

  const continueInMia = async () => {
    if (!onContinue) return;
    setContinuing(true);
    try {
      onContinue(await siftApiClient.continueRdsSummary(kind, id, loadConversationId('rds')));
    } catch {
      // Seeding failed: still open the chat on the summary, it just will not be in Mia's memory.
      if (text) onContinue({ prompt: FALLBACK_PROMPT[kind], reply: text });
    } finally {
      setContinuing(false);
    }
  };

  return (
    <section className="cv-mia-summary" aria-label="Mia: where to focus" aria-busy={phase === 'loading'}>
      <div className="cv-mia-summary-head">
        <span className="cv-mia-badge">Mia</span>
        <span className="cv-mia-summary-title">Where to focus</span>
      </div>
      {phase === 'loading' && (
        <div className="cv-mia-summary-loading" role="status">
          <span className="cv-mia-dots" aria-hidden="true"><i /><i /><i /></span>
          <span>{STEPS[kind][step]}</span>
          <div className="cv-mia-skeleton" aria-hidden="true"><span /><span /><span /></div>
        </div>
      )}
      {phase === 'done' && text && <p className="cv-mia-summary-text">{text}</p>}
      {phase === 'error' && (
        <p className="cv-mia-summary-error">
          Mia could not put a summary together right now.{' '}
          <button type="button" className="cv-mia-link" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </button>
        </p>
      )}
      {phase === 'done' && text && onContinue && (
        <button type="button" className="cv-mia-continue" disabled={continuing}
          onClick={() => void continueInMia()}>
          {continuing ? 'Opening Mia…' : 'Continue in Mia →'}
        </button>
      )}
    </section>
  );
}
